// Metric Availability Checker (JavaScript version)
// Determines what metrics are available based on actual data and configuration

export class MetricAvailabilityChecker {
  constructor(config) {
    this.config = config
  }

  // Core Metrics - Always available with varying quality
  checkAIIndexScore() {
    const prerequisites = []
    const missing = []

    if (this.config.hasLlmsTxt) {
      prerequisites.push('LLMS.txt file exists')
    } else {
      missing.push('LLMS.txt file')
    }

    if (this.config.trackedQueries > 0) {
      prerequisites.push(`${this.config.trackedQueries} queries tracked`)
    } else {
      missing.push('Tracked queries')
    }

    const dataQuality = this.calculateDataQuality([
      this.config.hasLlmsTxt ? 40 : 0,
      Math.min(30, (this.config.trackedQueries / 10) * 30),
      this.config.aiProvidersConfigured.length > 0 ? 30 : 10
    ])

    return {
      available: true,
      dataQuality,
      prerequisitesMet: prerequisites,
      prerequisitesMissing: missing,
      reason: missing.length > 0
        ? `Limited accuracy: ${missing.join(', ')} needed`
        : 'Full accuracy available',
      actionRequired: missing.length > 0 ? this.getFirstAction(missing) : undefined
    }
  }

  checkMentionAnalytics() {
    const hasQueries = this.config.trackedQueries > 0

    if (!hasQueries) {
      return {
        available: false,
        reason: 'No queries being tracked yet',
        actionRequired: 'Add queries to track',
        actionLink: '/search-tracking',
        prerequisitesMet: [],
        prerequisitesMissing: ['Tracked queries']
      }
    }

    const quality = Math.min(100, (this.config.trackedQueries / 20) * 100)

    return {
      available: true,
      dataQuality: quality,
      prerequisitesMet: [`${this.config.trackedQueries} queries tracked`],
      prerequisitesMissing: [],
      reason: quality < 50 ? 'Track more queries for better insights' : undefined,
      actionRequired: quality < 50 ? 'Add more queries' : undefined,
      actionLink: quality < 50 ? '/search-tracking' : undefined
    }
  }

  checkProviderMetrics() {
    const hasProviders = this.config.aiProvidersConfigured.length > 0

    if (!hasProviders) {
      return {
        available: false,
        reason: 'AI provider APIs not configured',
        actionRequired: 'Configure AI providers',
        actionLink: '/settings',
        prerequisitesMet: [],
        prerequisitesMissing: ['AI provider API keys']
      }
    }

    return {
      available: true,
      dataQuality: (this.config.aiProvidersConfigured.length / 5) * 100,
      prerequisitesMet: [
        `${this.config.aiProvidersConfigured.length} providers connected`,
        ...this.config.aiProvidersConfigured.map(p => `${p} API active`)
      ],
      prerequisitesMissing: []
    }
  }

  checkQueryPerformance() {
    const hasQueries = this.config.trackedQueries > 0
    const hasHistory = this.config.dataHistoryMonths > 0

    if (!hasQueries) {
      return {
        available: false,
        reason: 'No queries tracked yet',
        actionRequired: 'Start tracking queries',
        actionLink: '/search-tracking',
        prerequisitesMet: [],
        prerequisitesMissing: ['Tracked queries', 'Historical data']
      }
    }

    const quality = Math.min(100,
      (this.config.trackedQueries / 20) * 50 +
      (Math.min(this.config.dataHistoryMonths, 3) / 3) * 50
    )

    return {
      available: true,
      dataQuality: quality,
      prerequisitesMet: [
        `${this.config.trackedQueries} queries tracked`,
        `${this.config.dataHistoryMonths} months of data`
      ],
      prerequisitesMissing: [],
      reason: quality < 70 ? 'Accuracy improves with more data' : undefined
    }
  }

  checkContentEffectiveness() {
    const prerequisites = []
    const missing = []

    if (this.config.hasLlmsTxt) {
      prerequisites.push('LLMS.txt configured')
    } else {
      missing.push('LLMS.txt file')
    }

    return {
      available: true,
      dataQuality: this.config.hasLlmsTxt ? 80 : 20,
      prerequisitesMet: prerequisites,
      prerequisitesMissing: missing,
      reason: !this.config.hasLlmsTxt
        ? 'Create LLMS.txt for AI visibility'
        : undefined,
      actionRequired: !this.config.hasLlmsTxt
        ? 'Generate LLMS.txt'
        : undefined,
      actionLink: !this.config.hasLlmsTxt ? '/llms-txt' : undefined
    }
  }

  checkCompetitiveIntelligence() {
    const monthsRequired = 3
    const hasEnoughData = this.config.dataHistoryMonths >= monthsRequired
    const hasProviders = this.config.aiProvidersConfigured.length >= 2

    if (!hasEnoughData) {
      const monthsToWait = monthsRequired - this.config.dataHistoryMonths
      return {
        available: false,
        reason: `Requires ${monthsRequired} months of data`,
        actionRequired: 'Continue collecting data',
        estimatedTimeToAvailable: `${monthsToWait} month${monthsToWait > 1 ? 's' : ''}`,
        prerequisitesMet: [],
        prerequisitesMissing: [`${monthsRequired} months of historical data`]
      }
    }

    if (!hasProviders) {
      return {
        available: false,
        reason: 'Need multiple AI providers for comparison',
        actionRequired: 'Configure more AI providers',
        actionLink: '/settings',
        prerequisitesMet: [`${this.config.dataHistoryMonths} months of data`],
        prerequisitesMissing: ['Multiple AI providers']
      }
    }

    return {
      available: true,
      dataQuality: Math.min(100, (this.config.dataHistoryMonths / 6) * 100),
      prerequisitesMet: [
        `${this.config.dataHistoryMonths} months of data`,
        `${this.config.aiProvidersConfigured.length} providers connected`
      ],
      prerequisitesMissing: []
    }
  }

  checkTrendAnalysis() {
    const monthsRequired = 2
    const hasEnoughData = this.config.dataHistoryMonths >= monthsRequired

    if (!hasEnoughData) {
      const monthsToWait = monthsRequired - this.config.dataHistoryMonths
      return {
        available: false,
        reason: `Requires ${monthsRequired} months of data for trends`,
        actionRequired: 'Keep tracking queries',
        estimatedTimeToAvailable: `${monthsToWait} month${monthsToWait > 1 ? 's' : ''}`,
        prerequisitesMet: [],
        prerequisitesMissing: [`${monthsRequired} months of data`]
      }
    }

    const quality = Math.min(100, (this.config.dataHistoryMonths / 6) * 100)

    return {
      available: true,
      dataQuality: quality,
      prerequisitesMet: [`${this.config.dataHistoryMonths} months of data`],
      prerequisitesMissing: [],
      reason: quality < 50 ? 'Trends become more accurate over time' : undefined
    }
  }

  checkROIMetrics() {
    const hasAnalytics = this.config.integrationsEnabled?.analytics
    const hasRevenue = this.config.integrationsEnabled?.revenue

    const prerequisites = []
    const missing = []

    if (hasAnalytics) {
      prerequisites.push('Analytics connected')
    } else {
      missing.push('Analytics integration')
    }

    if (hasRevenue) {
      prerequisites.push('Revenue tracking connected')
    } else {
      missing.push('Revenue integration')
    }

    if (!hasAnalytics || !hasRevenue) {
      return {
        available: false,
        reason: 'Requires analytics and revenue integrations',
        actionRequired: 'Connect integrations',
        actionLink: '/settings',
        prerequisitesMet: prerequisites,
        prerequisitesMissing: missing
      }
    }

    return {
      available: true,
      dataQuality: 90,
      prerequisitesMet: prerequisites,
      prerequisitesMissing: []
    }
  }

  checkEngagementMetrics() {
    const hasAnalytics = this.config.integrationsEnabled?.analytics

    if (!hasAnalytics) {
      return {
        available: false,
        reason: 'Analytics integration required',
        actionRequired: 'Connect Google Analytics or similar',
        actionLink: '/settings',
        prerequisitesMet: [],
        prerequisitesMissing: ['Analytics integration']
      }
    }

    return {
      available: true,
      dataQuality: 85,
      prerequisitesMet: ['Analytics connected'],
      prerequisitesMissing: []
    }
  }

  checkAllMetrics() {
    return {
      aiIndexScore: this.checkAIIndexScore(),
      mentionAnalytics: this.checkMentionAnalytics(),
      providerMetrics: this.checkProviderMetrics(),
      queryPerformance: this.checkQueryPerformance(),
      contentEffectiveness: this.checkContentEffectiveness(),
      competitiveIntelligence: this.checkCompetitiveIntelligence(),
      trendAnalysis: this.checkTrendAnalysis(),
      roiMetrics: this.checkROIMetrics(),
      engagementMetrics: this.checkEngagementMetrics()
    }
  }

  getSetupProgress() {
    const steps = [
      {
        name: 'Configure Content',
        completed: (this.config.hasContent || false) && (this.config.hasEnrichedContent || false),
        required: true
      },
      {
        name: 'Create 3 Search Queries',
        completed: this.config.trackedQueries >= 3,
        required: true
      },
      {
        name: 'Generate LLMS',
        completed: this.config.hasLlmsTxt,
        required: true
      }
    ]

    const completed = steps.filter(s => s.completed).length
    const required = steps.filter(s => s.required && !s.completed)

    return {
      steps,
      completedCount: completed,
      totalCount: steps.length,
      percentage: (completed / steps.length) * 100,
      nextSteps: required.length > 0 ? required : steps.filter(s => !s.completed)
    }
  }

  calculateDataQuality(scores) {
    const total = scores.reduce((a, b) => a + b, 0)
    return Math.min(100, Math.round(total))
  }

  getFirstAction(missing) {
    if (missing.includes('LLMS.txt file')) return 'Create LLMS.txt'
    if (missing.includes('Tracked queries')) return 'Add queries to track'
    if (missing.includes('AI provider API keys')) return 'Configure AI providers'
    return 'Complete setup'
  }
}

export function getAvailabilityStatus(availability) {
  if (!availability.available) {
    return {
      status: 'locked',
      color: 'gray',
      icon: 'lock',
      message: availability.reason
    }
  }

  if (availability.dataQuality && availability.dataQuality < 50) {
    return {
      status: 'limited',
      color: 'yellow',
      icon: 'alert',
      message: 'Limited data available'
    }
  }

  if (availability.dataQuality && availability.dataQuality < 80) {
    return {
      status: 'partial',
      color: 'blue',
      icon: 'info',
      message: 'Good data quality'
    }
  }

  return {
    status: 'full',
    color: 'green',
    icon: 'check',
    message: 'Fully available'
  }
}